import { ref, computed } from 'vue';
import { GetBorrower } from '@/services/ReadyCashHome/GetBorrower';
import { GetWidget } from '@/services/ReadyCashHome/GetWidget';
import { GetLoanRanges } from '@/services/ReadyCashHome/GetLoanRanges';
import { WidgetStateEnum } from '@/enums/widgetState';
import {
  BorrowerDetailsInterface,
  TotalOutsandingBalance,
  TransactionHistoryTableDataInterface,
  ActiveRowTransactionHistoryInterface,
  LoanQuoteInterface,
  PostLoansRequestDetailsInterface,
  LoanReasonsArray,
} from '@/types/readyCashInterface';
import { GetTransactionHistory } from '@/services/ReadyCashHome/GetTransactionHistory';
import { GetReadyCashCompanySetting } from '@/services/ReadyCash/GetCompanySettings';
import { GetTotalOutstandingBalance } from '@/services/ReadyCashHome/GetTotalOutstandingBalance';
import { GetLoanReasons } from '@/services/ReadyCashHome/GetLoanReasons';
import { defineStore } from 'pinia';

export const useReadyCashHomeStore = defineStore('readyCashHome', () => {
  const LD_ReadyCashHome = ref<boolean>(false);
  const widgetState = ref<string>('');
  const isLoading = ref<boolean>(false);
  const errorStatusCode = ref<string>('');
  const showLoading = () => {
    isLoading.value = true;
  };
  const hideLoading = () => {
    isLoading.value = false;
  };
  const borrower = ref<BorrowerDetailsInterface>(null);
  const marketingSpiel = ref<string>('');
  const marketingSpielNote = ref<string>('');
  const marketingAmount = ref<number>();
  const minimumLoanAmount = ref<number>(0);
  const maximumLoanAmount = ref<number>(0);
  const selectedLoanAmount = ref<number>(0);
  const paymentTerms = ref<number[]>([]);
  const isreadyCashHomeTransactionDetailsOpen = ref<boolean>(false);
  const sortOrder = ref('desc');
  const chkShowActiveOnly = ref(false);
  const isTransactionListDetailsLoading = ref<boolean>(false);
  const transactionHistoryLoader = ref(true);
  const isGetCashPageEnabled = ref<boolean>(false);
  const isCancelModalVisible = ref<boolean>(false);
  const nextRoute = ref<object>(null);
  const isLoanTermsLoading = ref<boolean>(false);
  const isLoanQuoteLoading = ref<boolean>(false);
  const isSubmitModalOpen = ref<boolean>(false);
  const isSuccessPageEnabled = ref<boolean>(false);
  const loanReasons = ref<LoanReasonsArray>([]);
  const selectedReasonId = ref<number | null>(null);
  const selectedLoanPurpose = ref<string>('');
  const othersPurposeInputValue = ref<string>('');
  const selectedTerm = ref<number | null>(null);

  const hasBorrower = computed(() => !!borrower.value);

  const totalOutstandingBalance = ref<TotalOutsandingBalance>({
    totalRemainingBalance: 0,
    daysBeforeDueDate: 0,
    payments: [
      {
        dueDate: '2025-05-27T22:30:17.353Z',
        totalAmountDue: 0,
      },
    ],
  });

  const transactionHistoryTableData = ref<TransactionHistoryTableDataInterface>(
    {
      data: [
        {
          id: 0,
          referenceId: 'string',
          loanStatus: 'string',
          loanProviderName: 'string',
          interestAmount: 0,
          convenienceFee: 0,
          loanAmount: 0,
          loanTotalAmount: 0,
          totalAmountDue: 0,
          totalAmountPaid: 0,
          totalPenalty: 0,
          applicationDate: '2025-05-27T22:40:33.209Z',
          firstDueDate: '2025-05-27T22:40:33.209Z',
          lastDueDate: '2025-05-27T22:40:33.209Z',
          disbursementDate: '2025-05-27T22:40:33.209Z',
          reason: {
            category: 'string',
            additionalDetails: 'string',
          },
          payments: [
            {
              period: 0,
              amountDue: 0,
              dueDate: '2025-05-27T22:40:33.209Z',
              originalDueDate: '2025-05-27T22:40:33.209Z',
              paymentStatus: 'string',
              penalty: 0,
            },
          ],
          hrClientId: 0,
          hrCompanyId: 0,
          hrEmployeeId: 0,
          borrowerId: 0,
        },
      ],
    }
  );

  const transactionHistoryTablePagination = ref({
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
  });

  const activeRow = ref<ActiveRowTransactionHistoryInterface>({
    index: null,
    details: {
      id: 0,
      referenceId: 'string',
      loanStatus: 'string',
      loanProviderName: 'string',
      interestAmount: 0,
      convenienceFee: 0,
      loanAmount: 0,
      loanTotalAmount: 0,
      totalAmountDue: 0,
      totalAmountPaid: 0,
      totalPenalty: 0,
      applicationDate: '2025-05-27T22:40:33.209Z',
      firstDueDate: '2025-05-27T22:40:33.209Z',
      lastDueDate: '2025-05-27T22:40:33.209Z',
      disbursementDate: '2025-05-27T22:40:33.209Z',
      reason: {
        category: 'string',
        additionalDetails: 'string',
      },
      payments: [
        {
          period: 0,
          amountDue: 0,
          dueDate: '2025-05-27T22:40:33.209Z',
          originalDueDate: '2025-05-27T22:40:33.209Z',
          paymentStatus: 'string',
          penalty: 0,
        },
      ],
    },
  });

  const loanQuoteDetails = ref<LoanQuoteInterface>({
    loanAmount: 0,
    interest: 0,
    convenienceFee: 0,
    paymentPerPeriod: 0,
    total: 0,
    dueDates: [],
    referenceId: '',
    loanDocuments: [
      {
        name: '',
        url: '',
      },
    ],
  });

  const postLoansRequestDetails = ref<PostLoansRequestDetailsInterface>({
    id: 0,
    referenceId: '',
    loanStatus: '',
    loanProviderName: '',
    interestAmount: 0,
    convenienceFee: 0,
    loanAmount: 0,
    loanTotalAmount: 0,
    totalAmountDue: 0,
    totalAmountPaid: 0,
    totalPenalty: 0,
    applicationDate: '',
    firstDueDate: '',
    lastDueDate: '',
    disbursementDate: '',
    reason: {
      category: '',
      additionalDetails: '',
    },
    payments: [],
    hrClientId: 0,
    hrCompanyId: 0,
    hrEmployeeId: 0,
    borrowerId: 0,
  });

  const handleOnError = (error?: unknown) => {
    let errorStatus = '';
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response
    ) {
      errorStatus = (error.response as { status: string }).status;
    }
    widgetState.value = WidgetStateEnum.Error;
    errorStatusCode.value = errorStatus;
    hideLoading();

    if (errorStatus === '401') {
      throw new Error('Unauthorized access');
    }
  };

  const setBorrowerStoreValue = async () => {
    try {
      if (!hasBorrower.value) {
        const data = await GetBorrower();
        borrower.value = data;
      }
    } catch (error) {
      handleOnError(error);
    }
  };

  const getLoanRanges = async () => {
    try {
      const response = await GetLoanRanges();
      minimumLoanAmount.value = response.minimumAmount;
      maximumLoanAmount.value = response.maximumAmount;
      selectedLoanAmount.value = response.maximumAmount;
      paymentTerms.value = response.paymentTerms;
    } catch (error) {
      handleOnError(error);
    }
  };

  const initialize = async () => {
    try {
      const response = await GetWidget();
      widgetState.value = response.state;
    } catch (error) {
      handleOnError(error);
    }
  };

  const initializeBorrower = async () => {
    if (
      widgetState.value === WidgetStateEnum.BenefitsEmployee ||
      widgetState.value === WidgetStateEnum.BenefitsAdmin ||
      widgetState.value === WidgetStateEnum.Unconsented ||
      widgetState.value === WidgetStateEnum.Error
    ) {
      return;
    } else {
      await setBorrowerStoreValue();
      await getLoanRanges();
    }
  };

  const getReadyCashHomeCompanySetting = async () => {
    try {
      const response = await GetReadyCashCompanySetting();
      const data = response.data;
      marketingSpiel.value = data.marketingSpiel;
      marketingSpielNote.value = data.marketingSpielNote;
      marketingAmount.value = data.marketingAmount;
    } catch (error) {
      handleOnError(error);
    }
  };

  const getTotalOutstandingBalance = async () => {
    try {
      const response = await GetTotalOutstandingBalance();
      totalOutstandingBalance.value = response.data;
    } catch (error) {
      handleOnError(error);
    }
  };

  const getTransactionHistoryTableData = async () => {
    transactionHistoryLoader.value = true;
    try {
      const { currentPage, itemsPerPage } =
        transactionHistoryTablePagination.value.pagination;
      const response = await GetTransactionHistory({
        PageNumber: currentPage,
        RowsPerPage: itemsPerPage,
        SortOrder: sortOrder.value,
        Category: chkShowActiveOnly.value ? 'Active' : null,
      });
      transactionHistoryTableData.value.data = response.data.data;
      transactionHistoryTablePagination.value.pagination =
        response.data.pagination;

      transactionHistoryLoader.value = false;
    } catch (error) {
      handleOnError(error);
    }
  };

  const getLoanReasons = async () => {
    if (loanReasons.value.length > 0) return;
    try {
      const response = await GetLoanReasons();
      loanReasons.value = response as LoanReasonsArray;
    } catch (error) {
      handleOnError(error);
    }
  };

  return {
    LD_ReadyCashHome,
    widgetState,
    isLoading,
    showLoading,
    hideLoading,
    borrower,
    hasBorrower,
    setBorrowerStoreValue,
    handleOnError,
    initialize,
    initializeBorrower,
    getReadyCashHomeCompanySetting,
    marketingSpiel,
    marketingSpielNote,
    marketingAmount,
    getLoanRanges,
    minimumLoanAmount,
    maximumLoanAmount,
    selectedLoanAmount,
    paymentTerms,
    isreadyCashHomeTransactionDetailsOpen,
    isTransactionListDetailsLoading,
    activeRow,
    getTotalOutstandingBalance,
    totalOutstandingBalance,
    transactionHistoryTableData,
    getTransactionHistoryTableData,
    transactionHistoryTablePagination,
    sortOrder,
    chkShowActiveOnly,
    transactionHistoryLoader,
    isGetCashPageEnabled,
    isCancelModalVisible,
    nextRoute,
    loanQuoteDetails,
    isLoanQuoteLoading,
    isLoanTermsLoading,
    errorStatusCode,
    postLoansRequestDetails,
    isSubmitModalOpen,
    isSuccessPageEnabled,
    getLoanReasons,
    loanReasons,
    selectedLoanPurpose,
    othersPurposeInputValue,
    selectedReasonId,
    selectedTerm,
  };
});
